import { useQuery } from '@tanstack/react-query';
import { queryKeys } from './query-client';
import { PROXY_TOO_LARGE_STATUS, fetchRemoteDocument } from './remote-document';

// Fetches a remote `.md` / `.html` document's text for the in-app doc viewer.
//
// The request goes through `fetchRemoteDocument`, which owns two load-bearing options (`credentials:
// 'omit'` — WixMP attachment URLs are token-signed and need no cookie, and sending `wixSession2` to a media
// host would hand our session to a request that doesn't need it; `referrerPolicy: 'no-referrer'` — the URL
// we came from is itself a signed URL) AND the pod fallback for a host that refuses the browser CORS.

/** The document-size cap: **2 MB**, the same number on web, iOS and Android.
 *
 *  TWO UNITS, DELIBERATELY, and the name says bytes because that is the one that guards an allocation:
 *
 *  - **A download is capped in UTF-8 BYTES**, counted off the response stream as it arrives (see
 *    `fetchDocText`). Bytes are what we can measure before decoding, and bytes are what the tab has to
 *    allocate — the whole point is to stop the transfer before it costs memory.
 *  - **Inline content is capped in UTF-16 CHARACTERS** (`inlineDocTooLarge`), because by then the string
 *    already exists: it came out of the conversation payload we are holding, so re-encoding it to count
 *    bytes would allocate a second multi-MB copy to answer a question we only need approximately. UTF-8
 *    bytes ≥ UTF-16 code units for every string, so the character cap is the strictly *tighter* bound —
 *    it can refuse a document a byte cap would have allowed, never the reverse.
 *
 *  It is not a nicety on any surface: no markdown renderer here is virtualised (web's `Markdown` renders
 *  the whole tree, and native's `MarkdownBlocks`/`MarkdownMessage` are plain scrolling columns), so a
 *  40 MB agent-written log would be laid out as one enormous block list. Over the cap the viewer refuses
 *  politely — the "too large" state, with the download/share hand-off — and never attempts a render. */
export const MAX_DOC_PREVIEW_BYTES = 2 * 1024 * 1024;

/** Thrown when the document is over the preview cap — the viewer turns this into the "too large" state
 *  rather than the generic error, so the user is offered a download instead of a pointless Retry. */
export class DocTooLargeError extends Error {
  constructor(
    readonly bytes: number,
    readonly cap: number = MAX_DOC_PREVIEW_BYTES,
  ) {
    super(`Document is ${bytes} bytes, over the ${cap} byte preview cap`);
    this.name = 'DocTooLargeError';
  }
}

/** True when an ALREADY-DECODED document body is over the cap and must not be rendered. Inline bodies get
 *  the same refusal a download does — the hazard is the non-virtualised renderer, which is the same code
 *  whether the text arrived over the network or inside the message payload. Measured in characters; see
 *  the two-units note on `MAX_DOC_PREVIEW_BYTES`. */
export function inlineDocTooLarge(text: string, maxChars: number = MAX_DOC_PREVIEW_BYTES): boolean {
  return text.length > maxChars;
}

/** Reads at most `maxBytes` from a response body, THROWING the moment the cap is passed.
 *
 *  It refuses rather than truncates, and that is the behaviour, not caution: a truncated HTML report is cut
 *  mid-tag — the last thing `prepareHtmlDocument` wraps can be a half-open `<script>`, which swallows the
 *  rest of the document — and a truncated markdown report is a document that silently says something its
 *  author didn't. Neither is worth showing; "too large, here's the file" is honest.
 *
 *  The read is bounded as it goes: buffering the whole body first (a 60 MB chunked `.md` costs ~120 MB in
 *  the tab before any cap could look at it) is exactly what this avoids, so we pull chunks, count bytes, and
 *  `cancel()` the reader on the first chunk that crosses the cap — which aborts the transfer.
 *
 *  ONE loop, two callers: `readBounded` decodes the result as UTF-8 text (markdown/html/csv/text), and
 *  `lib/doc-bytes.ts` keeps the raw bytes for a PDF. Both therefore share the cap check, the `cancel()` and
 *  the refuse-never-truncate rule — which is the point of factoring it out rather than writing a second
 *  loop that drifts away from this one. */
// `Uint8Array<ArrayBuffer>` rather than the default `ArrayBufferLike`: every branch below allocates its own
// buffer, so the result is never SharedArrayBuffer-backed — and saying so is what lets a caller hand it
// straight to `new Blob([...])` without a copy or a cast.
export async function readBoundedBytes(res: Response, maxBytes: number): Promise<Uint8Array<ArrayBuffer>> {
  const body = res.body;
  if (!body?.getReader) {
    // No streaming body: a test double, or an environment without streaming responses. We cannot bound the
    // allocation here — but we still REFUSE over-cap rather than truncate, so the viewer's behaviour is
    // identical and only the peak memory of the refusal differs.
    //
    // `arrayBuffer()` first because it is the only lossless option for BINARY (a PDF read through `text()`
    // would come back mojibake). The `text()` path is kept for doubles that implement only that; every real
    // browser has `arrayBuffer`, so a PDF never takes it.
    const buffer =
      typeof res.arrayBuffer === 'function'
        ? new Uint8Array(await res.arrayBuffer())
        : new TextEncoder().encode(await res.text());
    if (buffer.byteLength > maxBytes) throw new DocTooLargeError(buffer.byteLength, maxBytes);
    return buffer;
  }

  const reader = body.getReader();
  const chunks: Uint8Array[] = [];
  let bytes = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;
    bytes += value.byteLength;
    if (bytes > maxBytes) {
      void reader.cancel();
      throw new DocTooLargeError(bytes, maxBytes);
    }
    chunks.push(value);
  }
  return concat(chunks, bytes);
}

async function readBounded(res: Response, maxBytes: number): Promise<string> {
  // Decoding happens ONCE over the concatenated bytes, so a multi-byte character split across two chunks is
  // reassembled correctly (and no code-unit `slice` can leave half a surrogate pair behind).
  return new TextDecoder('utf-8').decode(await readBoundedBytes(res, maxBytes));
}

function concat(chunks: Uint8Array[], bytes: number): Uint8Array<ArrayBuffer> {
  const all = new Uint8Array(bytes);
  let at = 0;
  for (const chunk of chunks) {
    all.set(chunk, at);
    at += chunk.byteLength;
  }
  return all;
}

export async function fetchDocText(url: string, maxBytes: number = MAX_DOC_PREVIEW_BYTES): Promise<string> {
  const res = await fetchRemoteDocument(url);
  // The pod refuses an over-cap document with 413; the direct path reaches the same verdict through the
  // bounded read below. Both must land on `DocTooLargeError` or the viewer offers a Retry that cannot help.
  if (res.status === PROXY_TOO_LARGE_STATUS) throw new DocTooLargeError(maxBytes, maxBytes);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  // Refuse before downloading when the host advertises the size. A `Content-Length` can be absent (chunked),
  // hidden (no CORS expose), or a lie — hence the bounded read below, which is the real guard.
  const advertised = Number(res.headers.get('content-length'));
  if (Number.isFinite(advertised) && advertised > maxBytes) throw new DocTooLargeError(advertised);

  return readBounded(res, maxBytes);
}

/** Thrown when the query is asked to run with no URL. `enabled: false` stops react-query from fetching on
 *  its own, but `refetch()` in v4 is NOT gated on `enabled` — so a Retry button over a null source used to
 *  call `fetch(null)`, i.e. a real request to the relative path `/null`. There is no Retry in that state any
 *  more (see `DocViewerModal`'s "unavailable"), and this makes the impossible call impossible rather than
 *  merely unreachable. */
export class DocSourceMissingError extends Error {
  constructor() {
    super('doc-text query has no URL to fetch');
    this.name = 'DocSourceMissingError';
  }
}

/** The React Query options for a document body — extracted so the null-URL guard is testable without a
 *  renderer (jest runs `testEnvironment: 'node'` in this package). */
export function docTextQueryOptions(url: string | null) {
  return {
    queryKey: queryKeys.docText(url ?? ''),
    queryFn: () => (url ? fetchDocText(url) : Promise.reject(new DocSourceMissingError())),
    enabled: !!url,
  };
}

/** The document body for `url`, or an idle query when there is nothing remote to fetch (inline content).
 *  `retry: false` is the app-wide default, so the viewer's explicit Retry button is the only recovery. */
export function useDocText(url: string | null) {
  return useQuery(docTextQueryOptions(url));
}
